import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Helmet } from "react-helmet";
import { css } from "@emotion/react";
import { useOutletContext } from "react-router";
import { useAuth } from "hooks";
import { APPLICANT_LIST_COLUMNS, MISSION_ROLE_STATES, STICKY_FILTER_KEYS_FOR_CLIENTS } from "constants/index";
import { Grid, PageWrapper } from "components/Containers";
import { RoleAtsReport, RoleLink } from "components/Missions/Roles";
import { Spinner } from "components/Loader";
import PageTitle from "components/PageTitle";
import {
  ApplicantBoardLayout,
  ApplicantListLayout,
  ApplicantPdfDownloadLink,
  ApplicationDetailsModalForClient,
  ApplicationProgressBanner,
  ApplicationRateForClient,
} from "components/Missions/Applicants";
import { ProfileLink } from "components/Profile";
import { MissionLink } from "components/Missions";
import { Banner } from "components/Banner";
import { PrimaryLink } from "components/Links";

/**
 * ClientMissionRoleApplications
 *
 * Customers entitled to the simplified portal land on the kanban board and can
 * toggle back to the list. Everyone else sees the list only.
 */
const ClientMissionRoleApplications = () => {
  const intl = useIntl();
  const { isShowcase, canUseApplicantBoard } = useAuth();
  const { selectedRole, loading } = useOutletContext();
  const [isBoardView, setIsBoardView] = useState(canUseApplicantBoard);

  if (loading) {
    return <Spinner />;
  }

  const showBoard = canUseApplicantBoard && isBoardView;

  return (
    <>
      <Helmet>
        <title>{intl.formatMessage({ id: "Meta.Missions.Applications.Title" })}</title>
      </Helmet>
      <PageWrapper>
        {isShowcase && (
          <Banner
            keyName="showClientApplicantsBanner"
            css={css`
              margin: 2rem 0 1rem 0;
            `}
          >
            <FormattedMessage
              id="Applicants.Banner.Title1"
              values={{
                intro: (
                  <b>
                    <FormattedMessage id="Applicants.Banner.Title2" />
                  </b>
                ),
              }}
            />
          </Banner>
        )}
        <Grid>
          <RoleAtsReport slug={selectedRole?.slug} />
          {canUseApplicantBoard ? (
            <Grid.col start={1} end={13}>
              <PageTitle
                subtitle={<FormattedMessage id="Missions.Mission.Role.Applications.Pipeline" />}
                actionBar={
                  <PrimaryLink onClick={() => setIsBoardView((prev) => !prev)} withMoreIcon>
                    <FormattedMessage id={`Missions.Applicants.SwitchViewButton.${isBoardView ? "List" : "Board"}`} />
                  </PrimaryLink>
                }
                css={styles.pipeline}
              />
            </Grid.col>
          ) : (
            <PageTitle
              subtitle={<FormattedMessage id="Missions.Mission.Role.Applications.Pipeline" />}
              css={styles.pipeline_without_toggle}
            />
          )}
          <Grid.col start={1} end={13}>
            {showBoard ? (
              <ApplicantBoardLayout
                defaultSelected={{
                  missionId: selectedRole?.mission?.id,
                  missionRoleIds: [selectedRole?.id],
                  organizationId: selectedRole?.mission?.organization?.id,
                }}
                initialStickyFilterKeys={STICKY_FILTER_KEYS_FOR_CLIENTS}
                hideMissionFilter={true}
                hideOrganizationFilter={true}
                hideMemberFilter={true}
                hideFilters={true}
                hideSorts={true}
                hideAdvancedFilters={false}
                variablesProp={{ includeScores: true, includeMission: true }}
                applicationModalComponent={ApplicationDetailsModalForClient}
                showBanner={selectedRole?.state === MISSION_ROLE_STATES.closed}
              />
            ) : (
              <ApplicantListLayout
                defaultSelected={{
                  missionId: selectedRole?.mission?.id,
                  missionRoleIds: [selectedRole?.id],
                  organizationId: selectedRole?.mission?.organization?.id,
                }}
                initialProgressMenu={2}
                showAdvancedOptions={false}
                hideAdvancedFilters={false}
                hideMemberFilter={true}
                hideOrganizationFilter={true}
                hideMissionFilter={true}
                hideFilters={true}
                hideTabs={true}
                hideSorts={true}
                enableCountQueries={true}
                variablesProp={{ includeScores: true }}
                columns={[
                  APPLICANT_LIST_COLUMNS.member,
                  APPLICANT_LIST_COLUMNS.mission,
                  APPLICANT_LIST_COLUMNS.checklistPercentage,
                  APPLICANT_LIST_COLUMNS.hourlyRateRange,
                  APPLICANT_LIST_COLUMNS.profileAvailability,
                  APPLICANT_LIST_COLUMNS.noticePeriod,
                  APPLICANT_LIST_COLUMNS.applied,
                  APPLICANT_LIST_COLUMNS.matchScore,
                  APPLICANT_LIST_COLUMNS.viewButton,
                ]}
                initialStickyFilterKeys={STICKY_FILTER_KEYS_FOR_CLIENTS}
                applicationModalComponent={ApplicationDetailsModalForClient}
                modalConfigFunc={(application) => ({
                  openModalOnClick: true,
                  withOverlay: false,
                  title: (
                    <FormattedMessage
                      id="Missions.MissionRole.ApplicationDetailsModal.Header"
                      values={{
                        org: <ProfileLink profile={application?.profile} />,
                        mission: <MissionLink data={application?.mission} withOverlay />,
                        role: (
                          <RoleLink mission={application?.mission} missionRole={application?.missionRole} withOverlay />
                        ),
                      }}
                    />
                  ),
                  info: (
                    <div css={styles.modal_info}>
                      <ApplicationProgressBanner missionRoleApplicant={application} css={styles.application_progress} />
                      <ApplicantPdfDownloadLink applicant={application} />
                      <ApplicationRateForClient application={application} />
                    </div>
                  ),
                  tabTitles: {
                    code_challenges: (
                      <FormattedMessage id="Missions.Applicants.ClientApplicationDetailsModal.TitleTechnicalAssessment" />
                    ),
                    profile: <FormattedMessage id="Missions.Applicants.ClientApplicationDetailsModal.TitleProfile" />
                  },
                  application,
                })}
              />
            )}
          </Grid.col>
        </Grid>
      </PageWrapper>
    </>
  );
};

const styles = {
  pipeline: css`
    margin-top: -4rem;
  `,
  pipeline_without_toggle: css`
    margin-top: -4rem;
    margin-bottom: -2rem;
  `,
  modal_info: css`
    display: flex;
    align-items: center;
    gap: 12px;
  `,
  application_progress: css`
    display: inline-flex;
    align-items: center;
    width: auto;
    height: auto;
    min-height: 0;
    margin: 0;
    padding: 0.6rem 1rem;
    border-radius: 0.6rem;
    white-space: nowrap;

    > div {
      display: flex;
      align-items: center;
      gap: 0.4rem;
    }
  `,
};

export default ClientMissionRoleApplications;
